import { PageTitle } from "components/PageTitle";
import { Loading } from "components/ui/Loading/Loading";
import {
  type CategoryUsage,
  useUsageHistory,
  useUsageSummary,
} from "hooks/useUsageApi";
import { RestApiError } from "lib/rest-client";
import { FileText } from "lucide-react";
import type { FC } from "react";
import { formatDate } from "utils/relative-time";

/**
 * カテゴリ別プログレスバーを表示するコンポーネント
 * @param {{ category: CategoryUsage }} props カテゴリ利用状況
 * @return {JSX.Element} プログレスバー
 */
const CategoryProgressBar: FC<{ category: CategoryUsage }> = ({ category }) => {
  const pct = category.limit > 0 ? (category.used / category.limit) * 100 : 0;
  const isExceeded = category.limit > 0 && category.used >= category.limit;
  const barColor = isExceeded
    ? "bg-error"
    : category.isWarning
      ? "bg-warning"
      : "bg-primary";

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="font-medium text-gray-700">{category.label}</span>
        <span className="text-sm text-gray-500">
          {category.limit > 0 ? (
            <>
              {category.used.toLocaleString()} /{" "}
              {category.limit.toLocaleString()} {category.unit}
            </>
          ) : (
            <>
              {category.used.toLocaleString()} {category.unit} (無制限)
            </>
          )}
        </span>
      </div>
      {category.limit > 0 && (
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`${barColor} h-3 rounded-full transition-all duration-300`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          ></div>
        </div>
      )}
      {isExceeded && (
        <p className="text-error text-xs mt-1">上限に達しました</p>
      )}
      {!isExceeded && category.isWarning && (
        <p className="text-warning text-xs mt-1">
          まもなく上限に達します ({Math.round(pct)}%)
        </p>
      )}
    </div>
  );
};

/**
 * テナントユーザー向け利用状況ページ
 * @return {JSX.Element} 利用状況ページ
 */
export const UsagePage: FC = () => {
  const { data: summary, loading, error } = useUsageSummary();
  const { data: history, loading: historyLoading } = useUsageHistory(6);

  if (error) {
    const isNoContract = error instanceof RestApiError && error.status === 404;

    if (isNoContract) {
      return (
        <div className="container mx-auto px-4 py-6">
          <PageTitle>利用状況</PageTitle>
          <div className="card bg-base-100 shadow-md">
            <div className="card-body items-center text-center py-12">
              <FileText className="w-12 h-12 text-base-content/30" />
              <p className="text-lg font-medium text-base-content/70 mt-2">
                契約情報が登録されていません
              </p>
              <p className="text-base-content/50 text-sm mt-1">
                利用状況を表示するには、契約の設定が必要です。管理者にお問い合わせください。
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>利用状況</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  if (loading || !summary) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>利用状況</PageTitle>
        <div className="flex justify-center py-8">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <PageTitle>利用状況</PageTitle>

      {/* 契約情報 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">契約情報</h2>
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">プラン</dt>
              <dd className="text-gray-900 font-semibold">
                {summary.contract.plan}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">ステータス</dt>
              <dd>
                <span
                  className={`badge ${
                    summary.contract.status === "active"
                      ? "badge-success"
                      : summary.contract.status === "trial"
                        ? "badge-info"
                        : "badge-ghost"
                  }`}
                >
                  {summary.contract.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">請求期間</dt>
              <dd className="text-gray-900">
                {formatDate(summary.contract.periodStart)} 〜{" "}
                {formatDate(summary.contract.periodEnd)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* カテゴリ別利用状況 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">カテゴリ別利用状況</h2>
          {summary.categories.map(cat => (
            <CategoryProgressBar key={cat.category} category={cat} />
          ))}
        </div>
      </div>

      {/* 利用履歴 */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h2 className="card-title text-color-title">利用履歴</h2>
          {historyLoading ? (
            <div className="flex justify-center py-4">
              <Loading size="md" />
            </div>
          ) : history && history.periods.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>期間</th>
                    {summary.categories.map(cat => (
                      <th key={cat.category} className="text-right">
                        {cat.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.periods.map((period, idx) => (
                    <tr
                      key={period.periodStart}
                      className={idx === 0 ? "font-semibold" : ""}
                    >
                      <td className="whitespace-nowrap">
                        {formatDate(period.periodStart)} 〜{" "}
                        {formatDate(period.periodEnd)}
                      </td>
                      {period.categories.map(cat => (
                        <td key={cat.category} className="text-right">
                          {cat.used.toLocaleString()}
                          {cat.limit > 0 && (
                            <span className="text-gray-400 text-xs">
                              /{cat.limit.toLocaleString()}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              利用履歴がありません
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsagePage;
